import { useRef } from 'react';

const ColorPicker = ({
    value,
    onChange,
    label,
    showHex = true,
    disabled = false,
    className = "",
    size = "default" // "small", "default", "large"
}) => {
    const colorInputRef = useRef(null);

    const handleColorChange = (e) => {
        const newColor = e.target.value;
        onChange(newColor);
    };

    const handleButtonClick = () => {
        if (!disabled && colorInputRef.current) {
            colorInputRef.current.click();
        }
    };

    const getSizeClasses = () => {
        switch (size) {
            case "small":
                return "w-8 h-8";
            case "large":
                return "w-12 h-12";
            default:
                return "w-10 h-10";
        }
    };

    const currentColor = value || '#4A90E2';

    return (
        <div className={`color-picker-container ${className}`}>
            {label && (
                <label className="text-sm font-medium text-gray-300 mb-3 block">
                    {label}
                </label>
            )}

            <div className="flex items-center space-x-3">
                {/* Color Preview Button */}
                <button
                    type="button"
                    onClick={handleButtonClick}
                    disabled={disabled}
                    className={`
                        ${getSizeClasses()}
                        rounded-full border-3 border-white shadow-lg cursor-pointer 
                        transition-all duration-200 hover:scale-105 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900
                        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl'}
                        relative overflow-hidden
                    `}
                    style={{ backgroundColor: currentColor }}
                    title={disabled ? 'Color picker disabled' : `Select ${label?.toLowerCase() || 'color'}`}
                >
                    {/* Disabled overlay */}
                    {disabled && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                            <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                    )}
                </button>

                {/* Hidden Color Input */}
                <input
                    ref={colorInputRef}
                    type="color"
                    value={currentColor}
                    onChange={handleColorChange}
                    disabled={disabled}
                    className="sr-only"
                    aria-label={label || 'Color picker'}
                />

                {/* Color Information */}
                {showHex && !disabled && (
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-300 font-mono uppercase">
                            {currentColor}
                        </span>
                        <span className="text-xs text-gray-500">
                            Click to change
                        </span>
                    </div>
                )}

                {disabled && (
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-400 font-mono uppercase">
                            {currentColor}
                        </span>
                        <span className="text-xs text-gray-500">
                            Currently disabled
                        </span>
                    </div>
                )}
            </div>

            {/* Additional help text for disabled state */}
            {disabled && label && (
                <p className="text-xs text-gray-500 mt-2">
                    This color option is currently disabled
                </p>
            )}
        </div>
    );
};

// Preset color suggestions component (optional quick colors)
export const ColorPresets = ({ onColorSelect, selectedColor, className = "" }) => {
    const presetColors = [
        '#4A90E2', '#9B59B6', '#E91E63', '#E74C3C',
        '#F39C12', '#F1C40F', '#2ECC71', '#2C3E50',
        '#FFFFFF', '#000000', '#95A5A6', '#34495E'
    ];

    return (
        <div className={`color-presets ${className}`}>
            <label className="text-xs font-medium text-gray-400 mb-2 block uppercase tracking-wider">
                Quick Colors
            </label>
            <div className="grid grid-cols-6 gap-2">
                {presetColors.map((color) => (
                    <button
                        key={color}
                        onClick={() => onColorSelect(color)}
                        className={`
                            w-8 h-8 rounded-lg border-2 transition-all duration-200 
                            hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-900
                            ${selectedColor === color
                                ? 'border-white shadow-lg scale-110'
                                : 'border-gray-600 hover:border-gray-400'
                            }
                        `}
                        style={{ backgroundColor: color }}
                        title={`Select ${color}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ColorPicker; 